import { FC, useEffect, useRef, useState } from "react";
import clsx from "classnames";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { CookieCategory } from "@onmo/graphql/graphql";
import { updateUserDeviceCookieConsentGQL } from "../../../graphql";
import { Dialog } from "../../core/Dialog/Dialog";
import { Button } from "../../core/Button/Button";
import { Icon } from "../../core/Icon/Icon";
import { Toggle } from "../../core/Toggle/Toggle";
import { Typography } from "../../core/Typography/Typography";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isGamepadMode, onCrossModeChange } from "../../../hooks/useCrossInput";
import { useCookieConsent } from "../../../hooks/useCookieConsent";
import {
  ACCEPT_ALL,
  ICookieConsent,
  REJECT_ALL,
  saveCookieConsent,
} from "../../../models/cookieConsent";
import {
  getCookiePolicyDocUrl,
  SCROLL_AMOUNT,
} from "../../../constants/settings";
import { isTV } from "../../../utils/platform";
import { getDeviceInfoWithSpecs } from "../../../utils/deviceDetection";
import { PageRoutes } from "../../../constants/routes";
import { useStyles } from "./CookieConsentPopup.style";
import { useStyles as useLegalStyles } from "../LegalInformationPanel/LegalInformationPanel.style";

type CookieConsentView = "privacy" | "configure" | "policy";

export const CookieConsentPopup: FC = () => {
  const classes = useStyles();
  const legalClasses = useLegalStyles();
  const { t, i18n } = useTranslation();
  const { setActiveElement, gamepads, gamepadEmitter } = useGamepadContext();
  const hasGamepad = !!gamepads?.length;
  const { hasChosen } = useCookieConsent();
  const [updateUserDeviceCookieConsent] = useMutation(
    updateUserDeviceCookieConsentGQL,
  );
  const { pathname } = useLocation();

  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const policyRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<CookieConsentView>("privacy");
  const [draft, setDraft] = useState<ICookieConsent>(REJECT_ALL);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [viewBeforePolicy, setViewBeforePolicy] =
    useState<CookieConsentView>("privacy");
  const [policyHtml, setPolicyHtml] = useState("");

  const isOpen =
    isMounted &&
    !hasChosen &&
    !isDismissed &&
    pathname !== PageRoutes.CookiePolicy;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const focusDefaultElement = () => {
      const defaultElement = dialogRef.current?.querySelector<HTMLElement>(
        '[data-gamepad="default-selected"]',
      );
      setActiveElement(defaultElement ?? undefined);
    };

    if (isTV || hasGamepad || isGamepadMode()) focusDefaultElement();

    // First remote/gamepad press after launch: focus the popup, not the page.
    return onCrossModeChange((mode) => {
      if (mode === "gamepad") requestAnimationFrame(focusDefaultElement);
    });
  }, [view, isOpen, hasGamepad, setActiveElement]);

  useEffect(() => {
    if (view !== "policy") return;
    const controller = new AbortController();
    fetch(getCookiePolicyDocUrl(i18n.language), { signal: controller.signal })
      .then((res) => res.text())
      .then(setPolicyHtml)
      .catch(() => undefined);
    return () => controller.abort();
  }, [view, i18n.language]);

  // The Ok button is the only focusable element, so up/down scroll the text.
  useEffect(() => {
    if (view !== "policy") return;
    const scrollPolicy = (action: string) => {
      if (action !== "up" && action !== "down") return;
      policyRef.current?.scrollBy({
        top: action === "down" ? SCROLL_AMOUNT : -SCROLL_AMOUNT,
        behavior: "smooth",
      });
    };
    gamepadEmitter.on("action", scrollPolicy);
    return () => {
      gamepadEmitter.off("action", scrollPolicy);
    };
  }, [view, gamepadEmitter]);

  const saveConsent = async (
    consent: ICookieConsent,
    consents: CookieCategory[],
  ) => {
    saveCookieConsent(consent);
    try {
      const deviceInfo = await getDeviceInfoWithSpecs();
      await updateUserDeviceCookieConsent({
        variables: { input: { consents, deviceInfo } },
      });
    } catch (error) {
      console.error("Failed to update device cookie consent:", error);
    }
  };

  const onAcceptAll = () =>
    saveConsent(ACCEPT_ALL, [
      CookieCategory.Necessary,
      CookieCategory.Functional,
      CookieCategory.Analytics,
    ]);

  const onRejectAll = () => saveConsent(REJECT_ALL, [CookieCategory.Necessary]);

  const onConfigure = () => {
    setDraft(REJECT_ALL);
    setView("configure");
  };

  const onSave = () =>
    saveConsent(draft, [
      CookieCategory.Necessary,
      ...(draft.functional ? [CookieCategory.Functional] : []),
      ...(draft.analytics ? [CookieCategory.Analytics] : []),
    ]);

  const onBack = () => setView("privacy");

  const onOpenPolicy = () => {
    setViewBeforePolicy(view);
    setView("policy");
  };

  const onClosePolicy = () => setView(viewBeforePolicy);

  // Closing without a choice keeps optional cookies off and asks again next launch.
  const onClose = () => {
    if (view === "policy") onClosePolicy();
    else if (view === "configure") onBack();
    else setIsDismissed(true);
  };

  const onToggleFunctional = () =>
    setDraft((prev) => ({ ...prev, functional: !prev.functional }));

  const onToggleAnalytics = () =>
    setDraft((prev) => ({ ...prev, analytics: !prev.analytics }));

  const renderCategory = (
    icon: string,
    title: string,
    description: string,
    control?: React.ReactNode,
  ) => (
    <>
      <Icon name={icon} size={32} className={classes.categoryIcon} />
      <div className={classes.categoryText}>
        <Typography
          variant={TypographyVariant.BodyB4Medium}
          className={classes.categoryTitle}
        >
          {title}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB6Regular}
          color={TypographyColor.Silver}
        >
          {description}
        </Typography>
      </div>
      {control}
    </>
  );

  const functionalTitle = t("CookieConsentPopup_Functional");
  const functionalDescription = t(
    "CookieConsentPopup_Enable features like support chat and game videos (e.g. YouTube).",
  );
  const analyticsTitle = t("CookieConsentPopup_Analytics");
  const analyticsDescription = t(
    "CookieConsentPopup_Help us understand usage and improve ONMO+.",
  );
  const changeLaterText = t(
    "CookieConsentPopup_You can change your preference at any time in Settings.",
  );

  const policyLink = (
    <Button
      variant="borderless"
      size="xSmall"
      className={classes.policyLink}
      data-gamepad="cookie-policy-link"
      onClick={onOpenPolicy}
    >
      {t("CookieConsentPopup_Read our Cookie Policy")}
    </Button>
  );

  return (
    <Dialog
      open={isOpen}
      dialogRef={dialogRef}
      onClose={onClose}
      data-dialog-nav="rows"
      title={t("CookieConsentPopup_We Value Your Privacy")}
      rootClassName={clsx(
        classes.root,
        view === "policy" && classes.rootPolicy,
      )}
      className={classes.content}
    >
      {view === "privacy" && (
        <>
          <Typography
            variant={TypographyVariant.HeadingH3Bold}
            className={classes.title}
          >
            {t("CookieConsentPopup_We Value Your Privacy")}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB3Medium}
            className={classes.description}
          >
            {t(
              "CookieConsentPopup_ONMO+ uses cookies and similar technologies to make our service work properly, improve your experience and understand how it's used.",
            )}
          </Typography>
          <div className={classes.categories}>
            <div className={classes.category}>
              {renderCategory(
                "icon-bold-setting-2",
                functionalTitle,
                functionalDescription,
              )}
            </div>
            <div className={classes.category}>
              {renderCategory(
                "icon-analytics",
                analyticsTitle,
                analyticsDescription,
              )}
            </div>
          </div>
          {policyLink}
          <Typography
            variant={TypographyVariant.BodyB5Medium}
            className={classes.note}
          >
            {changeLaterText}
          </Typography>
          <div className={classes.actions}>
            <Button
              className={classes.btn}
              variant="contained"
              data-gamepad="default-selected"
              onClick={onAcceptAll}
            >
              {t("CookieConsentPopup_Accept All")}
            </Button>
            <Button
              className={classes.btn}
              variant="outlined"
              color="secondary"
              data-selected-class={classes.outlinedSelected}
              data-gamepad="cookie-reject-all"
              onClick={onRejectAll}
            >
              {t("CookieConsentPopup_Reject All")}
            </Button>
            <Button
              className={classes.btn}
              variant="outlined"
              color="secondary"
              data-selected-class={classes.outlinedSelected}
              data-gamepad="cookie-configure"
              onClick={onConfigure}
            >
              {t("CookieConsentPopup_Configure")}
            </Button>
          </div>
        </>
      )}

      {view === "configure" && (
        <>
          <Typography
            variant={TypographyVariant.HeadingH3Bold}
            className={classes.title}
          >
            {t("CookieConsentPopup_Configure")}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB3Medium}
            className={classes.description}
          >
            {t("CookieConsentPopup_Choose which cookies you allow.")}
          </Typography>
          <div className={classes.categories}>
            <div className={classes.category}>
              {renderCategory(
                "icon-bold-shield-security",
                t("CookieConsentPopup_Necessary (Always Active)"),
                t(
                  "CookieConsentPopup_Essential for ONMO+ to work, including login, streaming, payments and settings.",
                ),
                <span className={classes.locked}>
                  <Icon name="icon-bold-lock-2" size={20} />
                  <Toggle
                    checked
                    disabled
                    aria-label={t(
                      "CookieConsentPopup_Necessary (Always Active)",
                    )}
                  />
                </span>,
              )}
            </div>
            <div className={classes.category}>
              {renderCategory(
                "icon-bold-setting-2",
                functionalTitle,
                functionalDescription,
                <Toggle
                  checked={draft.functional}
                  onChange={onToggleFunctional}
                  aria-label={functionalTitle}
                  data-gamepad="default-selected"
                  data-selected-class={classes.toggleSelected}
                />,
              )}
            </div>
            <div className={classes.category}>
              {renderCategory(
                "icon-analytics",
                analyticsTitle,
                analyticsDescription,
                <Toggle
                  checked={draft.analytics}
                  onChange={onToggleAnalytics}
                  aria-label={analyticsTitle}
                  data-gamepad="cookie-toggle-analytics"
                  data-selected-class={classes.toggleSelected}
                />,
              )}
            </div>
          </div>
          {policyLink}
          <Typography
            variant={TypographyVariant.BodyB5Medium}
            className={classes.note}
          >
            {changeLaterText}
          </Typography>
          <div className={classes.actions}>
            <Button
              className={classes.btn}
              variant="contained"
              data-gamepad="cookie-save"
              onClick={onSave}
            >
              {t("CookieConsentPopup_Save")}
            </Button>
            <Button
              className={classes.btn}
              variant="outlined"
              color="secondary"
              data-selected-class={classes.outlinedSelected}
              data-gamepad="cookie-back"
              data-button-sound="back"
              onClick={onBack}
            >
              {t("CookieConsentPopup_Back")}
            </Button>
          </div>
        </>
      )}

      {view === "policy" && (
        <>
          <Typography
            variant={TypographyVariant.HeadingH3Bold}
            className={classes.title}
          >
            {t("Settings_Cookie Policy")}
          </Typography>
          <div
            ref={policyRef}
            className={clsx(legalClasses.legalDoc, classes.policyDoc)}
            dangerouslySetInnerHTML={{ __html: policyHtml }}
          />
          <div className={classes.actions}>
            <Button
              className={classes.btn}
              variant="contained"
              data-gamepad="default-selected"
              onClick={onClosePolicy}
            >
              {t("CookieConsentPopup_Ok")}
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
};
